'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { SpartanProductCustomizeGradeList } from '@/app/lcv/components/sections/spartan/products/customize/configurator/grade-list';
import type { SpartanProductCustomizeGradePanelProps } from '@/app/lcv/components/sections/spartan/products/customize/configurator/grade-panel/types';
import { SpartanProductCustomizeGradeSelection } from '@/app/lcv/components/sections/spartan/products/customize/configurator/grade-selection';
import { SPARTAN_PRODUCT_CUSTOMIZE_DATA_GA, SPARTAN_PRODUCT_CUSTOMIZE_TRACKING } from '@/app/lcv/components/sections/spartan/products/customize/tracking';
import { getSpartanProductLenis } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/store';
import { useSpartanScrollRefresh } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/useSpartanScrollRefresh';
import { getSpartanRevealElementProps, SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET, SPARTAN_REVEAL_PRESET } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import * as ScrollArea from '@/app/lcv/components/sections/spartan/products/shared/scroll-area';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import { SpartanTabs } from '@/app/lcv/components/sections/spartan/shared/tabs';
import { SPARTAN_PRODUCT_CUSTOMIZE_LABELS } from '@/app/lcv/contents/spartan/products/shared/customize-common-fields.data';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
const GRADE_SCROLL_INSET_PX = 8;
const WHEEL_LINE_HEIGHT_PX = 16;
export const SpartanProductCustomizeGradePanel = t0 => {
  const $ = _c(63);
  const {
    theme: t1,
    models,
    selectedModel,
    onSelectModel,
    hasSubCategoryTabs,
    subCategories,
    selectedSubCategoryDocumentId,
    onSubCategoryChange
  } = t0;
  const theme = t1 === undefined ? "light" : t1;
  const isDark = theme === "dark";
  const refreshScroll = useSpartanScrollRefresh();
  const gradeRootRef = useRef(null);
  const gradeViewportRef = useRef(null);
  let t2;
  if ($[0] !== refreshScroll) {
    t2 = () => {
      refreshScroll();
    };
    $[0] = refreshScroll;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== models || $[3] !== refreshScroll) {
    t3 = [models, refreshScroll];
    $[2] = models;
    $[3] = refreshScroll;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const root = gradeRootRef.current;
      const viewport = gradeViewportRef.current;
      if (!root || !viewport) {
        return;
      }
      const handleWheel = event => {
        let deltaY = event.deltaY;
        if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
          deltaY = deltaY * WHEEL_LINE_HEIGHT_PX;
        } else {
          if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
            deltaY = deltaY * viewport.clientHeight;
          }
        }
        const maxScrollTop = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
        const nextScrollTop = Math.min(maxScrollTop, Math.max(0, viewport.scrollTop + deltaY));
        const canScrollNested = maxScrollTop > 0 && nextScrollTop !== viewport.scrollTop;
        if (canScrollNested) {
          event.preventDefault();
          event.stopPropagation();
          viewport.scrollTop = nextScrollTop;
          return;
        }
        if (getSpartanProductLenis()) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        window.scrollBy({
          top: deltaY,
          left: 0,
          behavior: "instant"
        });
      };
      root.addEventListener("wheel", handleWheel, {
        passive: false
      });
      return () => root.removeEventListener("wheel", handleWheel);
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const frameId = requestAnimationFrame(() => {
        const viewport_0 = gradeViewportRef.current;
        const selectedGrade = viewport_0?.querySelector("[aria-pressed=\"true\"]");
        if (!viewport_0 || !selectedGrade) {
          return;
        }
        const viewportRect = viewport_0.getBoundingClientRect();
        const gradeRect = selectedGrade.getBoundingClientRect();
        const visibleTop = viewportRect.top + GRADE_SCROLL_INSET_PX;
        const visibleBottom = viewportRect.bottom - GRADE_SCROLL_INSET_PX;
        if (gradeRect.top < visibleTop) {
          viewport_0.scrollTop = viewport_0.scrollTop - (visibleTop - gradeRect.top);
        } else {
          if (gradeRect.bottom > visibleBottom) {
            viewport_0.scrollTop = viewport_0.scrollTop + (gradeRect.bottom - visibleBottom);
          }
        }
      });
      return () => cancelAnimationFrame(frameId);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== models || $[9] !== selectedModel.documentId) {
    t7 = [models, selectedModel.documentId];
    $[8] = models;
    $[9] = selectedModel.documentId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useEffect(t6, t7);
  const t8 = isDark ? "lg:bg-gray-900" : "lg:bg-silver-100";
  let t9;
  if ($[11] !== t8) {
    t9 = cn("order-3 lg:order-0 lg:col-start-3 lg:row-start-1 lg:flex lg:h-0 lg:min-h-full lg:flex-col lg:gap-4 lg:overflow-hidden lg:rounded-lg lg:p-4", t8);
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.gradeSection
    });
    t11 = getSpartanRevealElementProps({
      order: 1,
      preset: SPARTAN_REVEAL_PRESET.collection,
      scrollOffset: SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET
    });
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  const t12 = isDark ? "text-white" : "text-gray-900";
  let t13;
  if ($[15] !== t12) {
    t13 = cn("text-sub-h3 hidden shrink-0 px-2 lg:block", t12);
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.gradeTitle
    });
    t15 = <span className="font-noto-bold">{SPARTAN_PRODUCT_CUSTOMIZE_LABELS.selectGrade}</span>;
    $[17] = t14;
    $[18] = t15;
  } else {
    t14 = $[17];
    t15 = $[18];
  }
  const t16 = ` (${models.length})`;
  let t17;
  if ($[19] !== t16) {
    t17 = <span className="text-body-2">{t16}</span>;
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== t13 || $[22] !== t17) {
    t18 = <p className={t13} {...t14}>{t15}{t17}</p>;
    $[21] = t13;
    $[22] = t17;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== hasSubCategoryTabs || $[25] !== isDark || $[26] !== onSubCategoryChange || $[27] !== selectedSubCategoryDocumentId || $[28] !== subCategories) {
    t19 = !!hasSubCategoryTabs && <div className="hidden shrink-0 lg:block" {...createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.subCategoryTabs
    })}><SpartanTabs variant={isDark ? "dark" : "filled"} value={selectedSubCategoryDocumentId} onChange={onSubCategoryChange} items={subCategories.map(_temp)} itemTestIdPrefix={SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.subCategoryTabItem} tracking={{
        eventName: SPARTAN_PRODUCTS_EVENT_NAME,
        group: SPARTAN_PRODUCT_CUSTOMIZE_DATA_GA.section,
        value: SPARTAN_PRODUCT_CUSTOMIZE_DATA_GA.subCategory
      }} /></div>;
    $[24] = hasSubCategoryTabs;
    $[25] = isDark;
    $[26] = onSubCategoryChange;
    $[27] = selectedSubCategoryDocumentId;
    $[28] = subCategories;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.gradeMobile
    });
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== hasSubCategoryTabs || $[32] !== models || $[33] !== onSelectModel || $[34] !== onSubCategoryChange || $[35] !== selectedModel || $[36] !== selectedSubCategoryDocumentId || $[37] !== subCategories || $[38] !== theme) {
    t21 = <div className="lg:hidden" {...t20}><SpartanProductCustomizeGradeSelection theme={theme} models={models} selectedModel={selectedModel} onSelectModel={onSelectModel} showSubCategoryTabs={hasSubCategoryTabs} subCategories={subCategories} selectedSubCategoryDocumentId={selectedSubCategoryDocumentId} onSubCategoryChange={onSubCategoryChange} /></div>;
    $[31] = hasSubCategoryTabs;
    $[32] = models;
    $[33] = onSelectModel;
    $[34] = onSubCategoryChange;
    $[35] = selectedModel;
    $[36] = selectedSubCategoryDocumentId;
    $[37] = subCategories;
    $[38] = theme;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const t22 = isDark ? "bg-gray-800" : "bg-white";
  let t23;
  if ($[40] !== t22) {
    t23 = cn("relative hidden min-h-0 flex-1 overflow-visible rounded-md lg:block", t22);
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.gradeDesktop
    });
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      overflowY: "auto"
    };
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== models || $[45] !== onSelectModel || $[46] !== selectedModel.documentId || $[47] !== theme) {
    t26 = <ScrollArea.Viewport ref={gradeViewportRef} className="scroll-allow absolute inset-0 p-2" style={t25}><SpartanProductCustomizeGradeList placement="desktop" theme={theme} models={models} selectedModelDocumentId={selectedModel.documentId} onSelectModel={onSelectModel} gap="gap-2" /></ScrollArea.Viewport>;
    $[44] = models;
    $[45] = onSelectModel;
    $[46] = selectedModel.documentId;
    $[47] = theme;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  const t27 = isDark ? "bg-gray-300 hover:bg-white" : "bg-silver-500 hover:bg-silver-900";
  let t28;
  if ($[49] !== t27) {
    t28 = cn("relative flex-1 cursor-grab rounded-full will-change-transform", t27);
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t28) {
    t29 = <ScrollArea.Scrollbar orientation="vertical" className="absolute top-2! bottom-2! hidden w-4 touch-none bg-transparent px-1.5 data-[orientation=vertical]:-right-4! lg:flex"><ScrollArea.Thumb className={t28} /></ScrollArea.Scrollbar>;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t23 || $[54] !== t26 || $[55] !== t29) {
    t30 = <ScrollArea.Root ref={gradeRootRef} className={t23} type="auto" {...t24}>{t26}{t29}</ScrollArea.Root>;
    $[53] = t23;
    $[54] = t26;
    $[55] = t29;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== t18 || $[58] !== t19 || $[59] !== t21 || $[60] !== t30 || $[61] !== t9) {
    t31 = <div className={t9} {...t10} {...t11}>{t18}{t19}{t21}{t30}</div>;
    $[57] = t18;
    $[58] = t19;
    $[59] = t21;
    $[60] = t30;
    $[61] = t9;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  return t31;
};
function _temp(category) {
  return {
    label: category.nameEn || category.nameTh || category.title || "",
    value: category.documentId
  };
}